import { isWinnerlessRow } from '@/draw/core/matchOutcome';
import type { DrawMatch, DrawStage } from '@/draw/types';
import type { PoolCut } from './poolView';
import { shortMatchCode } from '@/app/[locale]/event/[eventId]/libs/matchCode';

/** A cell for a real match carries its short match ID (A3), printed on the caption line. */
type MatchCell =
  | {
      kind: 'result';
      result: 'won' | 'lost';
      games: [number, number];
      gameLine: string;
      scorecardMatchId: string | null;
      retired: boolean;
    }
  | { kind: 'walkover'; result: 'won' | 'lost' }
  | { kind: 'void' } // dual withdrawal, not played
  | { kind: 'live'; games: [number, number]; scorecardMatchId: string | null }
  | { kind: 'scheduled'; iso: string }
  | { kind: 'open' }; // unscheduled, or no match for the pair
export type PoolCell = { kind: 'self' } | (MatchCell & { code?: string | null });
export type PoolStatus = 'notStarted' | 'inProgress' | 'complete';

const poolMatches = (stage: DrawStage): DrawMatch[] =>
  stage.rounds.flatMap((r) => r.matches).filter((x) => !x.isBye);

const when = (x: DrawMatch): string => x.eventMatchSchedule?.scheduled_datetime ?? '';

/** Later by schedule, then position. */
const byLatest = (a: DrawMatch, b: DrawMatch) => when(a).localeCompare(when(b)) || a.position - b.position;

const pairKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);

/** Completed games per side; an unfinished (in_progress) game is never counted won. */
export function matchGames(match: DrawMatch): { player1: number; player2: number } {
  if (match.gamesWon) return { player1: match.gamesWon.player1, player2: match.gamesWon.player2 };
  let player1 = 0;
  let player2 = 0;
  for (const g of match.score ?? []) {
    if (g.status === 'in_progress') continue;
    if (g.player1Score > g.player2Score) player1++;
    else if (g.player2Score > g.player1Score) player2++;
  }
  return { player1, player2 };
}

const isWalkover = (match: DrawMatch) =>
  match.outcome === 'walkover' || (match.outcome == null && match.isWalkover === true);

function cellFor(match: DrawMatch, rowId: string): PoolCell {
  return { ...matchCellFor(match, rowId), code: shortMatchCode(match.matchId) };
}

function matchCellFor(match: DrawMatch, rowId: string): MatchCell {
  const rowIsP1 = match.player1?.id === rowId;
  const won = matchGames(match);
  const mine = rowIsP1 ? won.player1 : won.player2;
  const theirs = rowIsP1 ? won.player2 : won.player1;
  const scorecardMatchId = match.scorecardMatchId ?? null;

  if (isWinnerlessRow({ outcome: match.outcome, is_walkover: match.isWalkover }) && !isWalkover(match)) {
    return { kind: 'void' };
  }
  if (isWalkover(match)) {
    return { kind: 'walkover', result: match.winner?.id === rowId ? 'won' : 'lost' };
  }
  if (match.status === 'completed') {
    // No winner and level games: nothing to credit either side.
    if (!match.winner && mine === theirs) return { kind: 'void' };
    const result = match.winner ? (match.winner.id === rowId ? 'won' : 'lost') : mine > theirs ? 'won' : 'lost';
    const gameLine = (match.score ?? [])
      .map((g) => (rowIsP1 ? `${g.player1Score}–${g.player2Score}` : `${g.player2Score}–${g.player1Score}`))
      .join(', ');
    return { kind: 'result', result, games: [mine, theirs], gameLine, scorecardMatchId, retired: match.outcome === 'retired' };
  }
  if (match.status === 'in_progress') return { kind: 'live', games: [mine, theirs], scorecardMatchId };
  const iso = match.eventMatchSchedule?.scheduled_datetime;
  return iso ? { kind: 'scheduled', iso } : { kind: 'open' };
}

export function poolGrid(stage: DrawStage, rowIds: string[]): PoolCell[][] {
  const byPair = new Map<string, DrawMatch>();
  for (const match of [...poolMatches(stage)].sort(byLatest)) {
    if (!match.player1 || !match.player2) continue;
    byPair.set(pairKey(match.player1.id, match.player2.id), match); // later sorts last, so it wins
  }
  return rowIds.map((rowId) =>
    rowIds.map((colId): PoolCell => {
      if (rowId === colId) return { kind: 'self' };
      const match = byPair.get(pairKey(rowId, colId));
      return match ? cellFor(match, rowId) : { kind: 'open' };
    }),
  );
}

export function poolProgress(stage: DrawStage): {
  played: number;
  total: number;
  status: PoolStatus;
  bestOf: number | null;
} {
  const matches = poolMatches(stage);
  const played = matches.filter((x) => x.status === 'completed').length;
  const live = matches.some((x) => x.status === 'in_progress');
  const status: PoolStatus =
    matches.length > 0 && played === matches.length ? 'complete' : live || played > 0 ? 'inProgress' : 'notStarted';
  const formats = matches.map((x) => x.bestOf ?? x.eventMatchSchedule?.best_of ?? null);
  const first = formats[0] ?? null;
  const bestOf = first !== null && formats.every((f) => f === first) ? first : null;
  return { played, total: matches.length, status, bestOf };
}

/** Unfinished matches: live first, then by time, then position, then unscheduled. */
export function poolUpcoming(stage: DrawStage): DrawMatch[] {
  const rank = (x: DrawMatch) => (x.status === 'in_progress' ? 0 : when(x) ? 1 : 2);
  return poolMatches(stage)
    .filter((x) => x.status !== 'completed' && (x.player1 || x.player2))
    .sort((a, b) => rank(a) - rank(b) || when(a).localeCompare(when(b)) || a.position - b.position);
}

/** First cut is where the pool advances; later cuts are consolation places. */
export function rankEdge(rank: number, cuts: PoolCut[]): 'advance' | 'later' | null {
  const index = cuts.findIndex((c) => rank >= c.from && rank <= c.to);
  if (index < 0) return null;
  return index === 0 ? 'advance' : 'later';
}
